import { useMemo, type ReactElement } from 'react'
import { cn } from '@util'
import { isNotEmpty } from '@terros/common'
import { Box, Group, SimpleGrid, Stack, Text } from '@mantine/core'
import { Color, SizePrimitives } from '@front/model'
import { AvatarBlurhash, type AvatarProps } from '@components'

export type LeaderboardDisplayRow = {
  rank: number
  name: string
  value: string
  avatar: Pick<AvatarProps, 'avatarBlurhash' | 'avatarUrl' | 'color' | 'initials'>
}

export function LeaderboardDisplay({ rows }: { rows: LeaderboardDisplayRow[] }): ReactElement {
  const { podiumRows, leaderboardRows } = useMemo(
    () => ({
      podiumRows: rows.slice(0, 3),
      leaderboardRows: rows.slice(3),
    }),
    [rows]
  )

  return (
    <Stack>
      <LeaderboardPodium rows={podiumRows} />
      {isNotEmpty(leaderboardRows) && <LeaderboardList rows={leaderboardRows} />}
    </Stack>
  )
}

function LeaderboardPodium({ rows }: { rows: LeaderboardDisplayRow[] }): ReactElement {
  return (
    <SimpleGrid cols={3} spacing='xs' verticalSpacing='xs' className='items-end'>
      <PodiumPlace row={rows.at(1)} />
      <PodiumPlace row={rows.at(0)} />
      <PodiumPlace row={rows.at(2)} />
    </SimpleGrid>
  )
}

function PodiumPlace({ row }: { row: LeaderboardDisplayRow | undefined }): ReactElement {
  if (!row) return <Box />

  const isFirst = row.rank === 1

  return (
    <Stack align='center' gap={4} pb={isFirst ? 'sm' : 0} className='min-w-0'>
      <Box className='relative'>
        <Box
          className='w-fit rounded-full outline-[3px] outline-solid'
          style={{ outlineColor: getLeaderboardRankColor(row.rank) }}
        >
          <LeaderboardAvatar row={row} size={isFirst ? SizePrimitives['12'] : SizePrimitives['10']} />
        </Box>
        <RankBadge rank={row.rank} size={24} className='absolute -top-2 -right-2' />
      </Box>
      <Text fw={isFirst ? 700 : 600} lh={1.15} lineClamp={2} size={isFirst ? 'sm' : 'xs'} ta='center'>
        {row.name}
      </Text>
      <Text c='dimmed' fw={700} lh={1} size='xs' className='tabular-nums'>
        {row.value}
      </Text>
    </Stack>
  )
}

function LeaderboardList({ rows }: { rows: LeaderboardDisplayRow[] }): ReactElement {
  return (
    <Stack gap={0}>
      {rows.map((row) => (
        <LeaderboardListRow key={row.rank} row={row} />
      ))}
    </Stack>
  )
}

function LeaderboardListRow({ row }: { row: LeaderboardDisplayRow }): ReactElement {
  return (
    <Group
      gap='xs'
      justify='space-between'
      wrap='nowrap'
      className='border-b border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-default)] px-[var(--mantine-spacing-xs)] py-[var(--mantine-spacing-xs)] first:rounded-t-sm last:rounded-b-sm last:border-b-0'
    >
      <Group gap='xs' wrap='nowrap' className='min-w-0'>
        <Text c='dimmed' fw={700} miw={28} size='xs' className='tabular-nums'>
          {row.rank}
        </Text>
        <LeaderboardAvatar row={row} size='xs' />
        <Text size='sm' truncate>
          {row.name}
        </Text>
      </Group>
      <Text fw={700} size='sm' className='tabular-nums'>
        {row.value}
      </Text>
    </Group>
  )
}

function LeaderboardAvatar({ row, size }: { row: LeaderboardDisplayRow; size: AvatarProps['size'] }): ReactElement {
  return <AvatarBlurhash {...row.avatar} size={size} />
}

function RankBadge({ rank, size, className }: { rank: number; size: number; className?: string }): ReactElement {
  return (
    <Group
      align='center'
      className={cn('rounded-full text-[var(--mantine-color-dark-9)]', className)}
      justify='center'
      h={size}
      style={{ backgroundColor: getLeaderboardRankColor(rank) }}
      w={size}
    >
      <Text fw={800} lh={1} size='xs' className='tabular-nums'>
        {rank}
      </Text>
    </Group>
  )
}

function getLeaderboardRankColor(rank: number): string {
  if (rank === 1) return Color.gold
  if (rank === 2) return Color.silver
  return Color.bronze
}
